import React, { useEffect, useState } from 'react';
import { useDealServiceStore } from '../../lib/stores/dealServiceStore';
import { formatCurrency } from '../../lib/utils';
import { DollarSign, Info } from 'lucide-react';

interface DealServiceCostPreviewProps {
  serviceId: string;
  serviceName: string;
}

export function DealServiceCostPreview({ serviceId, serviceName }: DealServiceCostPreviewProps) {
  const { fetchServiceCostPreview } = useDealServiceStore();
  const [costs, setCosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadCosts() {
      setLoading(true);
      try {
        const templates = await fetchServiceCostPreview(serviceId);
        setCosts(templates);
      } catch (error) {
        console.error('Error loading cost preview:', error);
      } finally {
        setLoading(false);
      }
    }

    if (serviceId) {
      loadCosts();
    }
  }, [serviceId, fetchServiceCostPreview]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-2">
        <div className="h-3 bg-black/[0.06] dark:bg-white/[0.06] rounded w-1/2"></div>
        <div className="h-8 bg-black/[0.04] dark:bg-white/[0.04] rounded"></div>
      </div>
    );
  }

  if (costs.length === 0) {
    return null;
  }

  const total = costs.reduce((sum, cost) => sum + (cost.default_amount || 0), 0);

  return (
    <div className="text-[13px]">
      {/* Hint Notion-like: testo discreto, no card heavy */}
      <p className="text-[11.5px] text-black/55 dark:text-white/55 mb-2">
        Costi interni applicati automaticamente quando l'offerta viene accettata.
        Non visibili al cliente, servono al calcolo del margine.
      </p>

      <div className="rounded-md border border-black/[0.06] dark:border-white/[0.08] overflow-hidden">
        {costs.map((cost, idx) => (
          <div
            key={cost.id}
            className={`flex justify-between items-center px-2.5 py-1.5 ${
              idx !== 0 ? 'border-t border-black/[0.05] dark:border-white/[0.06]' : ''
            }`}
          >
            <div className="min-w-0 flex-1 flex items-center gap-2">
              <span className="text-black dark:text-white truncate">{cost.name}</span>
              {cost.cost_category && (
                <span className="text-[11px] text-black/45 dark:text-white/45 truncate">
                  · {cost.cost_category.name}
                </span>
              )}
            </div>
            <span className="text-black/75 dark:text-white/75 tabular-nums whitespace-nowrap">
              {formatCurrency(cost.default_amount || 0)}
            </span>
          </div>
        ))}
        <div className="flex justify-between items-center px-2.5 py-1.5 border-t border-black/[0.08] dark:border-white/[0.1] bg-black/[0.02] dark:bg-white/[0.03]">
          <span className="text-black/65 dark:text-white/65 font-medium">Totale costi interni</span>
          <span className="text-black dark:text-white font-semibold tabular-nums">{formatCurrency(total)}</span>
        </div>
      </div>
    </div>
  );
}
